import { ComplexField as ComplexFieldDefinition } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import {
  GenericFormData,
  RepeatedValidationFieldConfig,
  ValidationErrors,
  addRepeated,
  removeRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import {
  FieldContext,
  extractFieldValidation,
  getDefaultValue,
  isFieldEnabled,
} from '@platform-ui/utilities/application/fields/index.js';
import cx from 'classnames';
import { useCallback } from 'react';
import FullWidthPanel from '../../../components/FullWidthPanel/index.js';
import AlternatingRow from './AlternatingRow.js';
import Field from './Field.js';
import { FieldProps } from './types.js';

export default function ComplexField<FormData extends GenericFormData>({
  field: { key, copy, subFields, useLabelPrefix = true },
  validation,
  answers,
  dispatch,
  errors,
  ...props
}: FieldProps<ComplexFieldDefinition, FormData>): JSX.Element {
  const removeEntry = useCallback(
    (index: number): void => dispatch(removeRepeated(key, index)),
    [dispatch, key],
  );
  const addEntry = useCallback(
    (): void =>
      dispatch(
        addRepeated(
          key,
          Object.fromEntries(
            subFields.map((subField) => [subField.key, getDefaultValue(subField)]),
          ),
        ),
      ),
    [dispatch, key, subFields],
  );

  const isShortForm = subFields.length <= 4;

  return (
    <FullWidthPanel>
      <div className={cx('flex flex-col w-full lg:items-center')}>
        <div className={cx('w-auto m-auto', isShortForm && 'max-w-[36rem]')}>
          {answers[key].map((answer: FormData, index: number) => (
            <AlternatingRow key={`field.key-${index + 1}`}>
              <div
                className={cx('w-full flex flex-col gap-4 px-7 py-6', isShortForm && 'md:flex-row')}
              >
                <div
                  className={cx([
                    'flex items-center gap-x-5',
                    {
                      'md:flex-col md:justify-center md:items-start md:gap-y-2': isShortForm,
                    },
                  ])}
                >
                  <Typography variant="h3">{`${copy} ${index + 1}`}</Typography>
                  <Button
                    variant="text"
                    textColor={Color.Link}
                    onClick={(): void => removeEntry(index)}
                  >
                    Remove
                  </Button>
                </div>
                <div
                  className={cx(
                    'gap-4',
                    isShortForm ? 'grid md:grow md:flex-col md:grid-cols-1' : 'flex flex-wrap',
                  )}
                >
                  {subFields
                    .filter((field) => isFieldEnabled(field, answer))
                    .map(
                      (subField): JSX.Element => (
                        <Field
                          {...props}
                          context={FieldContext.Complex}
                          key={subField.key}
                          field={subField}
                          labelPrefix={useLabelPrefix ? `${copy} ${index + 1}` : ''}
                          validation={extractFieldValidation(
                            subField,
                            (validation as RepeatedValidationFieldConfig<FormData>)?.schema,
                          )}
                          repeated={{ parentKey: key, index }}
                          answers={answer}
                          dispatch={dispatch}
                          errors={
                            (errors[key][index] ?? {}) as ValidationErrors<FormData[typeof key]>
                          }
                        />
                      ),
                    )}
                </div>
              </div>
            </AlternatingRow>
          ))}
          <div className="px-7 py-4">
            <Button variant="text" textColor={Color.Link} onClick={addEntry}>
              + Add {copy}
            </Button>
          </div>
        </div>
      </div>
    </FullWidthPanel>
  );
}
